import {
  PlatePosition,
  ReactSelectableFast,
  SelectionProcess,
  SelectionType,
  WorklistValues,
} from "features/WorklistTools/shared/interfaces";
import {
  arrangeColWise,
  arrangeRowWise,
  checkForDoubleSelectionWithMultipleDestSelection,
  getWellSelectionType,
  stampSourceToDestination,
} from "features/WorklistTools/shared/WorklistHelpers";
import { Alert, AlertType } from "shared-components/toast";
import { NucleofectionToolActions, WorklistValuesState } from "../../state";
import { INucleofectionInternalState } from "../../state/initial-state";

export const selectDestWells = async (
  nucleofectionInternalState: INucleofectionInternalState,
  destPlateInfo: any,
  worklistValues: WorklistValuesState,
  dispatch: (action: { type: string; payload: any }) => void,
  selectedWells: ReactSelectableFast[],
  isConfirmed: (
    prompt: string,
    bullets?: string[] | undefined
  ) => Promise<boolean>
) => {
  const destSelection = selectedWells.map((well) => ({
    plateIndex: well.props.plateIndex,
    plateWellId: well.props.plateWellId,
    isSelectable: false,
  }));

  if (destSelection.length === 0) return;
  if (nucleofectionInternalState.selectedIntermediateWells.length === 0) {
    Alert({
      type: AlertType.ERROR,
      message: "You must select an Intermediate well",
    });
    return;
  }

  const selectionType = getWellSelectionType(
    nucleofectionInternalState.selectedIntermediateWells,
    destSelection
  );
  const sourcePlateIndex =
    nucleofectionInternalState.selectedIntermediateWells[0].plateIndex;
  let wellMapping: WorklistValues[] = [];

  switch (selectionType) {
    case SelectionType.NoSourceWellSelected:
      Alert({
        type: AlertType.ERROR,
        message: "Please select a source well(s)",
      });
      break;
    case SelectionType.SelectMultipleSourceAndDestWells:
      Alert({
        type: AlertType.ERROR,
        message: "You can only select 1 Destination well",
      });
      break;
    case SelectionType.SelectMultipleSourceAndOneDestWell:
      switch (nucleofectionInternalState.destSelectionProcess) {
        case SelectionProcess.Stamp:
          wellMapping = stampSourceToDestination(
            worklistValues.intToDest,
            nucleofectionInternalState.selectedIntermediateWells,
            destSelection[0],
            PlatePosition.Destination,
            false //no pooling
          );
          if (wellMapping.length) {
            addDestinationWellMapping(dispatch, wellMapping, worklistValues);
          } else {
            Alert({
              type: AlertType.ERROR,
              message: "Selection does not fit in Destination plate",
            });
            return;
          }
          break;
        case SelectionProcess.RowWise:
          wellMapping = await arrangeRowWise(
            worklistValues.intToDest,
            nucleofectionInternalState.selectedIntermediateWells,
            destSelection[0],
            destPlateInfo[destSelection[0].plateIndex].rows,
            destPlateInfo[destSelection[0].plateIndex].cols,
            PlatePosition.Destination,
            false //no pooling
          );
          if (wellMapping.length) {
            addDestinationWellMapping(dispatch, wellMapping, worklistValues);
          } else {
            Alert({
              type: AlertType.ERROR,
              message: "Selection does not fit in Destination plate",
            });
            return;
          }
          break;
        case SelectionProcess.ColWise:
          wellMapping = await arrangeColWise(
            worklistValues.intToDest,
            nucleofectionInternalState.selectedIntermediateWells,
            destSelection[0],
            destPlateInfo[destSelection[0].plateIndex].rows,
            destPlateInfo[destSelection[0].plateIndex].cols,
            PlatePosition.Destination,
            false //no pooling
          );
          if (wellMapping.length) {
            addDestinationWellMapping(dispatch, wellMapping, worklistValues);
          } else {
            Alert({
              type: AlertType.ERROR,
              message: "Selection does not fit in Destination plate",
            });
            return;
          }
          break;
      }
      break;
    case SelectionType.SelectOneSourceAndOneDestWell:
      {
        const error = checkForDoubleSelectionWithMultipleDestSelection(
          worklistValues.intToDest,
          [
            {
              plateIndex: destSelection[0].plateIndex,
              plateWellId: destSelection[0].plateWellId,
              isSelectable: true,
            },
          ]
        );
        if (error) {
          Alert({
            type: AlertType.ERROR,
            message: "Well has already been selected",
          });
          break;
        }
        if (
          nucleofectionInternalState.selectedIntermediateWells[0].isSelectable
        ) {
          addDestinationWellMapping(
            dispatch,
            [
              {
                sourcePlateIndex: sourcePlateIndex,
                sourceWellId:
                  nucleofectionInternalState.selectedIntermediateWells[0]
                    .plateWellId,
                destPlateIndex: destSelection[0].plateIndex,
                destWellId: destSelection[0].plateWellId,
                transferVol: "",
              },
            ],
            worklistValues
          );
        }
      }
      break;
    case SelectionType.SelectOneSourceAndMultipleDestWells: {
      const error = checkForDoubleSelectionWithMultipleDestSelection(
        worklistValues.intToDest,
        destSelection
      );
      if (error) {
        Alert({
          type: AlertType.ERROR,
          message: "Well has already been selected",
        });
        break;
      }
      for (const row of destSelection) {
        if (
          nucleofectionInternalState.selectedIntermediateWells[0].isSelectable
        ) {
          wellMapping.push({
            sourcePlateIndex: sourcePlateIndex,
            sourceWellId:
              nucleofectionInternalState.selectedIntermediateWells[0]
                .plateWellId,
            destPlateIndex: row.plateIndex,
            destWellId: row.plateWellId,
            transferVol: "",
          });
        }
      }
      if (wellMapping.length > 0) {
        addDestinationWellMapping(dispatch, wellMapping, worklistValues);
      }
      break;
    }
  }
};

export const addDestinationWellMapping = (
  dispatch: (action: { type: string; payload: any }) => void,
  wellMapping: WorklistValues[],
  worklistValues: WorklistValuesState
) => {
  const intWellAlreadyInDestPlate = worklistValues.intToDest.some(
    (existingIntToDest: any) =>
      wellMapping.some(
        (newMapping) =>
          existingIntToDest.destPlateIndex === newMapping.destPlateIndex &&
          existingIntToDest.sourcePlateIndex === newMapping.sourcePlateIndex &&
          existingIntToDest.sourceWellId === newMapping.sourceWellId
      )
  );

  if (intWellAlreadyInDestPlate) {
    return Alert({
      type: AlertType.ERROR,
      message: "Int well(s) can only be harvested once into Destination plate",
    });
  }

  dispatch(
    NucleofectionToolActions.INT_TO_DEST_ADDED({
      selections: wellMapping,
    })
  );
};
